import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '@/api/auth'
import { usersApi } from '@/api/users'
import type { User, LoginRequest, RegisterRequest, AuthResponse } from '@/types'
import type { Role } from '@/api/users'

export const useAuthStore = defineStore('auth', () => {
  // 状态
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const roles = ref<Role[]>([])
  const permissions = ref<string[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // 计算属性
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const hasPermission = computed(() => (permission: string) => {
    return permissions.value.includes(permission) || isAdmin.value
  })
  const hasRole = computed(() => (role: string) => {
    return user.value?.role === role
  })

  // 操作
  const setAuth = (authData: AuthResponse) => {
    console.log('setAuth 被调用，参数:', authData)
    
    if (!authData || !authData.access_token || !authData.user) {
      console.error('setAuth: 无效的认证数据', authData)
      throw new Error('无效的认证数据')
    }
    
    try {
      user.value = authData.user
      token.value = authData.access_token
      
      console.log('setAuth: 用户和token已设置')
      
      // 更新权限
      updatePermissions()
      
      // 持久化存储
      localStorage.setItem('access_token', authData.access_token)
      localStorage.setItem('user', JSON.stringify(authData.user))
      
      console.log('setAuth: 认证信息已保存到localStorage')
    } catch (err) {
      console.error('setAuth: 设置认证信息时出错', err)
      throw err
    }
  }

  const clearAuth = () => {
    user.value = null
    token.value = null
    roles.value = []
    permissions.value = []
    error.value = null
    
    // 清除持久化存储
    localStorage.removeItem('access_token')
    localStorage.removeItem('user')
  }

  const login = async (credentials: LoginRequest): Promise<void> => {
    try {
      isLoading.value = true
      error.value = null
      
      const authData = await authApi.login(credentials)
      setAuth(authData)
    } catch (err: any) {
      error.value = err.response?.data?.message || '登录失败'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const register = async (userData: RegisterRequest): Promise<void> => {
    try {
      isLoading.value = true
      error.value = null
      
      const authData = await authApi.register(userData)
      setAuth(authData)
    } catch (err: any) {
      error.value = err.response?.data?.message || '注册失败'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const logout = async (): Promise<void> => {
    try {
      authApi.logout()
      clearAuth()
    } catch (err: any) {
      console.error('Logout error:', err)
    }
  }

  const refreshToken = async (): Promise<void> => {
    try {
      const authData = await authApi.refreshToken()
      if (authData?.access_token) {
        // 只更新token，不更新用户信息
        token.value = authData.access_token
        localStorage.setItem('access_token', authData.access_token)
        console.log('Token已刷新')
      }
    } catch (err: any) {
      console.error('刷新token失败:', err)
      // 如果刷新失败，不要立即清除认证信息
      // 让用户继续使用当前token，直到真正过期
    }
  }

  const updateProfile = async (): Promise<void> => {
    try {
      const userData = await authApi.getProfile()
      user.value = userData
      localStorage.setItem('user', JSON.stringify(userData))
      
      // 更新权限
      await updatePermissions()
    } catch (err: any) {
      console.error('Update profile error:', err)
    }
  }

  const updatePermissions = async (): Promise<void> => {
    try {
      if (isAdmin.value) {
        // 管理员拥有所有权限
        permissions.value = ['*']
      } else {
        // 普通用户只有基本权限
        permissions.value = ['quiz:read', 'question:read', 'wrong:manage']
      }
    } catch (err: any) {
      console.error('Update permissions error:', err)
      permissions.value = []
    }
  }

  const fetchRoles = async (): Promise<Role[]> => {
    try {
      const rolesList = await usersApi.getRoles()
      roles.value = rolesList
      return rolesList
    } catch (err: any) {
      console.error('Fetch roles error:', err)
      return []
    }
  }

  const restoreAuth = (): void => {
    const storedToken = localStorage.getItem('access_token')
    const storedUser = localStorage.getItem('user')
    
    if (storedToken && storedUser) {
      try {
        token.value = storedToken
        user.value = JSON.parse(storedUser)
        
        // 更新权限
        updatePermissions()
        
        // 验证token是否仍然有效
        authApi.verifyToken().catch(() => {
          clearAuth()
        })
      } catch {
        clearAuth()
      }
    }
  }

  const clearError = (): void => {
    error.value = null
  }

  return {
    // 状态
    user: computed(() => user.value),
    token: computed(() => token.value),
    roles: computed(() => roles.value),
    permissions: computed(() => permissions.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    
    // 计算属性
    isAuthenticated,
    isAdmin,
    hasPermission,
    hasRole,
    
    // 操作
    setAuth,
    login,
    register,
    logout,
    refreshToken,
    updateProfile,
    updatePermissions,
    fetchRoles,
    restoreAuth,
    clearError,
    clearAuth,
  }
})